import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from './Navbar';
import Footer from './Footer';
import ContactModal from './ContactModal';
import { Helmet } from 'react-helmet-async';
import './Home.css';

// Counter Component for Number Animation
const Counter = ({ target, duration = 2000, suffix = "" }) => {
    const [count, setCount] = useState(0);
    const [isVisible, setIsVisible] = useState(false);
    const counterRef = React.useRef(null); // Used in useEffect

    React.useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                }
            },
            { threshold: 0.1 }
        );

        const currentElement = counterRef.current;
        if (currentElement) {
            observer.observe(currentElement);
        }

        return () => {
            if (currentElement) {
                observer.unobserve(currentElement);
            }
        };
    }, []);

    React.useEffect(() => {
        if (!isVisible) return;

        let startTime = null;
        let animationFrame;

        const animate = (timestamp) => {
            if (!startTime) startTime = timestamp;
            const progress = timestamp - startTime;
            const percentage = Math.min(progress / duration, 1);

            // Easing function for smooth counting
            const easeOutQuart = 1 - Math.pow(1 - percentage, 4);

            setCount(Math.floor(easeOutQuart * target));

            if (progress < duration) {
                animationFrame = requestAnimationFrame(animate);
            } else {
                setCount(target);
            }
        };

        animationFrame = requestAnimationFrame(animate);

        return () => cancelAnimationFrame(animationFrame);
    }, [isVisible, target, duration]);

    return (
        <span ref={counterRef}>
            {count}{suffix}
        </span>
    );
};

const Home = ({ user, setUser }) => {
    const navigate = useNavigate();
    const [isModalOpen, setIsModalOpen] = useState(false);

    /* const handleLogout = () => {
        localStorage.removeItem('cwUser');
        setUser(null);
        navigate('/');
    }; */

    return (
        <div className="home-container">
            <Helmet>
                <title>Collaboration World - Home</title>
                <meta name="description" content="Connect Brands with Creators. A comprehensive collaboration and influencer marketing ecosystem designed to drive brand awareness and engagement." />
                <link rel="canonical" href="https://www.collaborationworld.in/" />
            </Helmet>

            {/* Navigation Header */}
            <Navbar user={user} setUser={setUser} />

            {/* Hero Section */}
            <section className="hero-section">
                <div className="hero-content">
                    <div className="hero-est">EST. 2020</div>
                    <h1 className="hero-title">
                        Connect Brands with <span className="gradient-text">Creators</span>
                    </h1>
                    <p className="hero-subtitle">
                      An AI-powered platform that is a comprehensive collaboration and influencer marketing ecosystem designed to drive brand awareness, engagement, and conversions through strategic creator partnerships.
                    </p>
                    <div className="hero-buttons">
                        {user ? (
                            <button onClick={() => navigate('/dashboard')} className="btn-primary-large">
                                Go to Dashboard
                            </button>
                        ) : (
                            <button onClick={() => navigate('/register')} className="btn-primary-large">
                                Start Collaborating
                                <span className="btn-subtitle">*for influencers*</span>
                            </button>
                        )}
                        <button onClick={() => navigate('/about')} className="btn-secondary-large">
                            Learn More
                        </button>
                    </div>
                </div>
                <div className="hero-visual">
                    <div className="floating-card card-1">
                        <div className="card-icon"><i className="ri-arrow-right-line"></i></div>
                        <span className="card-text">Strategic Partnerships</span>
                    </div>
                    <div className="floating-card card-2">
                        <div className="card-icon"><i className="ri-arrow-right-up-line"></i></div>
                        <span className="card-text">Measurable Impact</span>
                    </div>
                    <div className="floating-card card-3">
                        <div className="card-icon"><i className="ri-shining-line"></i></div>
                        <span className="card-text">End-to-End Management</span>
                    </div>
                </div>
            </section>

            {/* Features Section */}
            <section className="features-section">
                <h2 className="section-title">Why Choose Collaboration World?</h2>
                <div className="features-timeline">
                    <svg className="connection-lines" viewBox="0 0 1200 800" preserveAspectRatio="none">
                        <path className="line line-1" d="M 200,150 Q 400,100 600,200" />
                        <path className="line line-2" d="M 600,200 Q 800,300 1000,250" />
                        <path className="line line-3" d="M 200,450 Q 400,400 600,500" />
                        <path className="line line-4" d="M 600,500 Q 800,600 1000,550" />
                    </svg>

                    <div className="feature-node node-1">
                        <div className="node-icon"><i className="ri-shining-line"></i></div>
                        <div className="node-content">
                            <h3>Strategic Matching</h3>
                            <p>Connect with creators whose audience perfectly aligns with your brand</p>
                        </div>
                        <div className="node-glow"></div>
                    </div>

                    <div className="feature-node node-2">
                        <div className="node-icon"><i className="ri-focus-2-line"></i></div>
                        <div className="node-content">
                            <h3>Full Lifecycle</h3>
                            <p>End-to-end management from onboarding to deliverables</p>
                        </div>
                        <div className="node-glow"></div>
                    </div>

                    <div className="feature-node node-3">
                        <div className="node-icon"><i className="ri-arrow-right-up-line"></i></div>
                        <div className="node-content">
                            <h3>Measurable Results</h3>
                            <p>Track performance with clear metrics and reporting</p>
                        </div>
                        <div className="node-glow"></div>
                    </div>

                    <div className="feature-node node-4">
                        <div className="node-icon"><i className="ri-shining-line"></i></div>
                        <div className="node-content">
                            <h3>Quality Control</h3>
                            <p>Consistent standards ensure successful execution</p>
                        </div>
                        <div className="node-glow"></div>
                    </div>

                    <div className="feature-node node-5">
                        <div className="node-icon"><i className="ri-focus-2-line"></i></div>
                        <div className="node-content">
                            <h3>Scalable Solutions</h3>
                            <p>Grow your marketing without operational burden</p>
                        </div>
                        <div className="node-glow"></div>
                    </div>

                    <div className="feature-node node-6">
                        <div className="node-icon"><i className="ri-star-line"></i></div>
                        <div className="node-content">
                            <h3>Professional Network</h3>
                            <p>Access curated creators with proven engagement</p>
                        </div>
                        <div className="node-glow"></div>
                    </div>
                </div>
            </section>

            {/* Work Statistics Section */}
            <section className="work-stats-section">
                <div className="stats-header">
                    <h2 className="stats-title">OUR WORK STATISTICS</h2>
                    <p className="stats-subtitle">Collaboration World Is A Solid Investment In Your Growth.</p>
                </div>

                <div className="stats-grid">
                    <div className="stat-card">
                        <div className="stat-icon-wrapper">
                            <i className="ri-team-line"></i>
                        </div>
                        <h3 className="stat-number">
                            <Counter target={100} suffix=" +" />
                        </h3>
                        <p className="stat-label">HAPPY CLIENTS</p>
                    </div>

                    {/* <div className="stat-card">
                        <div className="stat-icon-wrapper">
                            <i className="ri-heart-3-line"></i>
                        </div>
                        <h3 className="stat-number">
                            <Counter target={500} suffix=" +" />
                        </h3>
                        <p className="stat-label">PROJECTS DELIVERED</p>
                    </div> */}

                    {/* <div className="stat-card">
                        <div className="stat-icon-wrapper">
                            <i className="ri-medal-line"></i>
                        </div>
                        <h3 className="stat-number">
                            <Counter target={50} suffix=" +" />
                        </h3>
                        <p className="stat-label">CLIENT 5 STAR RATING</p>
                    </div> */}

                    <div className="stat-card">
                        <div className="stat-icon-wrapper">
                            <i className="ri-global-line"></i>
                        </div>
                        <h3 className="stat-number">
                            <Counter target={10} suffix=" X" />
                        </h3>
                        <p className="stat-label">MAXIMUM ROI</p>
                    </div>



                    <div className="stat-card">
                        <div className="stat-icon-wrapper">
                            <i className="ri-group-line"></i>
                        </div>
                        <h3 className="stat-number">
                            <Counter target={10000} suffix=" +" duration={3000} />
                        </h3>
                        <p className="stat-label">CREATOR NETWORK</p>
                    </div>

                    <div className="stat-card">
                        <div className="stat-icon-wrapper">
                            <i className="ri-user-follow-line"></i>
                        </div>
                        <h3 className="stat-number">
                            <Counter target={75} suffix=" %" duration={3000} />
                        </h3>
                        <p className="stat-label">REPEAT CLIENTS</p>
                    </div>
                </div>
            </section>

            {/* How It Works Section */}
            <section className="how-it-works-section">
                <h2 className="section-title">How It Works</h2>
                <div className="steps-container">
                    <div className="step">
                        <div className="step-number">1</div>
                        <h3>Define Your Goals</h3>
                        <p>Share your campaign objectives, target audience, and desired outcomes with our team.</p>
                    </div>
                    <div className="step-arrow"><i className="ri-arrow-right-line"></i></div>
                    <div className="step">
                        <div className="step-number">2</div>
                        <h3>Get Matched</h3>
                        <p>We connect you with creators whose audience and style align with your brand values.</p>
                    </div>
                    <div className="step-arrow"><i className="ri-arrow-right-line"></i></div>
                    <div className="step">
                        <div className="step-number">3</div>
                        <h3>Launch Campaign</h3>
                        <p>We manage briefing, timelines, and coordination while you focus on your business.</p>
                    </div>
                    <div className="step-arrow"><i className="ri-arrow-right-line"></i></div>
                    <div className="step">
                        <div className="step-number">4</div>
                        <h3>Track Results</h3>
                        <p>Monitor performance, receive deliverables, and measure campaign impact in real-time.</p>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="cta-section">
                <div className="cta-blob blob-1"></div>
                <div className="cta-blob blob-2"></div>
                <div className="cta-blob blob-3"></div>

                <div className="cta-wrapper">
                    <div className="cta-text-content">
                        <div className="cta-sparkle"><i className="ri-sparkling-line"></i></div>
                        <h2 className="cta-heading">
                            <span className="word-animate">Ready</span>{' '}
                            <span className="word-animate">to</span>{' '}
                            <span className="word-animate gradient-word">Transform</span>{' '}
                            <span className="word-animate">Your</span>{' '}
                            <span className="word-animate gradient-word">Brand?</span>
                        </h2>
                        <p className="cta-description">Join hundreds of successful brands building authentic creator partnerships</p>

                        {user ? (
                            <button onClick={() => navigate('/contact')} className="cta-button-3d">
                                <span className="button-text">Start Your Journey</span>
                                <div className="button-glow"></div>
                                <p className="brand-only-text">*For Brands Only*</p>
                            </button>

                        ) : (
                            <button onClick={() => navigate('/contact')} className="cta-button-3d">
                                <span className="button-text">Start Your Journey</span>
                                <div className="button-glow"></div>
                                <p className="brand-only-text">*For Brands Only*</p>
                            </button>
                        )}

                        <div className="cta-trust-badges">
                            <div className="trust-badge">
                                <span className="badge-icon"><i className="ri-check-line"></i></span>
                                <span>Quality Assured</span>
                            </div>
                            <div className="trust-badge">
                                <span className="badge-icon"><i className="ri-check-line"></i></span>
                                <span>Customized Plans</span>
                            </div>
                            <div className="trust-badge">
                                <span className="badge-icon"><i className="ri-check-line"></i></span>
                                <span>24/7 Support</span>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <Footer />
            <ContactModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
        </div>
    );
};

export default Home;
